import { ENDPOINTS, GOOGLE_MAP_SETTINGS } from "@constants";
import AbstractService from "./AbstractService";
import FeatureFlagService from "./FeatureFlagService";
import NexusService from "./NexusService";

export default class LocationService extends AbstractService {
	static endpoint = ENDPOINTS.locations;

	static locationsApi = NexusService.locationsApi;

	static apiCoreEndpointsFeature = "use_api_core_endpoints_locations";

	static getById(id) {
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			return this.locationsApi.getLocation({ id }).then(response => ({ data: response }));
		}

		return this.get({
			endpoint: `${this.endpoint}/${id}`,
		});
	}

	static delete(id) {
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			return this.locationsApi.deleteLocation({ id });
		}

		return this.del({
			endpoint: `${this.endpoint}/${id}`,
		});
	}

	static save({
		locationId,
		name,
		streetAddress1,
		city,
		county,
		stateId,
		locationTypeId,
		zipcode,
		latitude,
		longitude,
		googlePlaceId,
		projectId,
		clientSupervisorContactId,
		customCoordinates,
		organizationFee,
		ZoneId,
	}) {
		const isCreating = locationId === undefined;

		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			if (isCreating) {
				const locationCreatePayload = {
					Name: name,
					StreetAddress1: streetAddress1,
					City: city,
					County: county,
					StateId: stateId,
					LocationTypeId: locationTypeId,
					Zipcode: zipcode,
					Latitude: latitude,
					Longitude: longitude,
					GooglePlaceId: googlePlaceId,
					ProjectId: projectId,
					ClientSupervisorContactId: clientSupervisorContactId,
					CustomCoordinates: customCoordinates,
					OrganizationFee: organizationFee,
					ZoneId,
				};

				return this.locationsApi
					.createLocation({ locationCreatePayload })
					.then(response => ({ data: response }));
			}

			const updateReq = {
				id: locationId,
				locationUpdatePayload: {
					Id: locationId,
					Name: name,
					StreetAddress1: streetAddress1,
					City: city,
					County: county,
					StateId: stateId,
					LocationTypeId: locationTypeId,
					Zipcode: zipcode,
					Latitude: latitude,
					Longitude: longitude,
					GooglePlaceId: googlePlaceId,
					ProjectId: projectId,
					ClientSupervisorContactId: clientSupervisorContactId,
					CustomCoordinates: customCoordinates,
					OrganizationFee: organizationFee,
					ZoneId,
				},
			};

			return this.locationsApi
				.updateLocation(updateReq)
				.then(response => ({ data: response }));
		}

		const method = isCreating ? "POST" : "PUT";
		const endpoint = `${this.endpoint}/${!isCreating ? locationId : ""}`;

		return this.request({
			method,
			endpoint,
			data: {
				locationId,
				name,
				streetAddress1,
				city,
				county,
				stateId,
				locationTypeId,
				zipcode,
				latitude,
				longitude,
				googlePlaceId,
				projectId,
				clientSupervisorContactId,
				customCoordinates,
				organizationFee,
				ZoneId,
			},
		});
	}

	static getAll({
		minimalData,
		includeDeleted,
		pageNumber,
		projectId,
		displayCount,
		clientId,
		organizationId,
		officerId,
		includeStats,
	}) {
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			const getReq = {
				organizationId,
				clientId,
				projectId,
				minimalData,
				includeDeleted,
				officerId,
			};

			if (pageNumber !== undefined && !Number.isNaN(pageNumber)) {
				getReq.pageNumber = pageNumber;

				if (displayCount !== undefined && !Number.isNaN(displayCount)) {
					getReq.displayCount = displayCount;
				}

				return this.locationsApi
					.getPaginatedLocations(getReq)
					.then(response => ({ data: response }));
			}

			return this.locationsApi.getLocations(getReq).then(response => ({ data: response }));
		}

		return this.get({
			endpoint: this.endpoint,
			queryParams: {
				minimalData,
				includeDeleted,
				pageNumber,
				displayCount,
				clientId,
				organizationId,
				projectId,
				officerId,
				includeStats,
			},
		});
	}

	static getGeoLocation(address) {
		return this.get({
			baseURL: GOOGLE_MAP_SETTINGS.geocoderURL,
			withCredentials: false,
			headers: {},
			cache: { maxAge: 86400000 }, // 24-hour cache on unique location searches
			queryParams: {
				key: GOOGLE_MAP_SETTINGS.key,
				address,
			},
		});
	}

	static getOfficers(id, queryParams) {
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			const getReq = {
				locationId: id,
			};

			if (queryParams && Object.hasOwn(queryParams, "pageNumber")) {
				getReq.pageNumber = queryParams.pageNumber;
			}

			if (queryParams && Object.hasOwn(queryParams, "displayCount")) {
				getReq.displayCount = queryParams.displayCount;
			}

			return this.locationsApi
				.getPaginatedOfficersForLocation(getReq)
				.then(response => ({ data: response }));
		}

		return this.get({ endpoint: `${this.endpoint}/${id}/officers`, queryParams });
	}

	static getJobs(id, queryParams) {
		if (FeatureFlagService.isEnabled(this.apiCoreEndpointsFeature)) {
			const getReq = {
				locationId: id,
			};

			if (queryParams) {
				if (Object.hasOwn(queryParams, "pageNumber")) {
					getReq.pageNumber = queryParams.pageNumber;
				}
				if (Object.hasOwn(queryParams, "displayCount")) {
					getReq.displayCount = queryParams.displayCount;
				}
				if (Object.hasOwn(queryParams, "status")) {
					getReq.status = queryParams.status;
				}
			}

			return this.locationsApi
				.getPaginatedJobEventsForLocation(getReq)
				.then(response => ({ data: response }));
		}

		return this.get({
			endpoint: `${this.endpoint}/${id}/jobevents`,
			queryParams,
		});
	}
}
